// src/oura/visibility/usePriorScan.ts
//
// The previous scan the Competitors tab compares against.
//
// Only a scan that asked the SAME questions qualifies: the history records a
// hash of each scan's question set (`visibility.questionSetId`), and two scans
// share it only when every question is identical. A scan with no hash on
// record, or one measured after a change of method, gives no comparison at
// all, which is the trend-break rule every other delta on the page follows.
//
// Fetched only while the tab is open, through the same GET /assessments/{id}
// the version selector uses, and reduced to rival counts through the same
// model builder the screen uses, so last month is counted exactly like today.

import { useCallback, useEffect, useState } from "react";
import { getAssessment, type AeoScoreHistoryEntry } from "@service/aeo";
import { buildVisModel, visibilityInputsFromAssessment, type LatestAssessment, type VisibilityInputs } from "./fromLive";
import { priorScanFrom, type VisPriorScan } from "./rivalFacts";

/** The last complete scan before this one on the same question set, or null. */
export function comparableScan(history: AeoScoreHistoryEntry[], assessmentId: string | null): AeoScoreHistoryEntry | null {
    const now = history.find((h) => h.assessment_id === assessmentId);
    const set = now?.visibility?.questionSetId;
    if (!now || !set || now.visibility?.trendBreak) return null;
    return (
        history
            .filter((h) => h.assessment_id !== now.assessment_id)
            .filter((h) => (h.status ?? "complete") === "complete" && h.captured_at < now.captured_at)
            .filter((h) => h.visibility?.questionSetId === set)
            .sort((a, b) => b.captured_at.localeCompare(a.captured_at))[0] ?? null
    );
}

/**
 * Where the comparison stands. "none" is the only state that means there is
 * nothing to compare with; a request that fails, or a scan that comes back
 * without its visibility block, is "error", never folded into "none", so an
 * outage cannot pass for "no change to report" (Jack's review on #3952).
 */
export type PriorScanStatus = "none" | "loading" | "ready" | "error";

export interface PriorScanState {
    status: PriorScanStatus;
    /** Set only when `status` is "ready". */
    prior: VisPriorScan | null;
    /** The comparison scan's date, known from the history before it loads. */
    capturedAt: string | null;
    /** Ask for the comparison scan again after an error. */
    retry: () => void;
}

const noop = () => {};

export function usePriorScan({
    inputs,
    fixture,
    enabled,
}: {
    inputs: VisibilityInputs | null;
    /** A sample business carries its previous scan in the fixture. */
    fixture: boolean;
    /** Only while the Competitors tab is open. */
    enabled: boolean;
}): PriorScanState {
    // Keyed by the scan it belongs to, so a change of target never shows the
    // old scan's result for one render under the new scan's date.
    const [loaded, setLoaded] = useState<{ targetId: string | null; status: PriorScanStatus; prior: VisPriorScan | null }>({
        targetId: null,
        status: "none",
        prior: null,
    });
    const [attempt, setAttempt] = useState(0);
    const retry = useCallback(() => setAttempt((n) => n + 1), []);
    const target = !fixture && inputs ? comparableScan(inputs.history, inputs.assessmentId) : null;
    const targetId = target?.assessment_id ?? null;

    useEffect(() => {
        if (fixture || !enabled || !targetId || !target || !inputs) {
            setLoaded({ targetId: null, status: "none", prior: null });
            return;
        }
        const requestId = targetId;
        let live = true;
        setLoaded({ targetId: requestId, status: "loading", prior: null });
        getAssessment(requestId)
            .then((a) => {
                if (!live) return;
                // GET /assessments/{id} returns the same shape as
                // `latest_assessment` (the version selector relies on it too).
                const past = visibilityInputsFromAssessment(a as unknown as LatestAssessment, {
                    capturedAt: target.captured_at,
                    history: inputs.history,
                });
                // The history said this scan measured visibility on the same
                // questions, so a scan with no visibility block is a broken
                // read, not an absent comparison.
                const model = past.vis ? buildVisModel(past, null) : null;
                setLoaded(model ? { targetId: requestId, status: "ready", prior: priorScanFrom(model) } : { targetId: requestId, status: "error", prior: null });
            })
            // A thrown model build lands here too.
            .catch(() => { if (live) setLoaded({ targetId: requestId, status: "error", prior: null }); });
        return () => { live = false; };
        // `target` and `inputs` are read for the chosen id only; the id decides.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fixture, enabled, targetId, attempt]);

    if (fixture) {
        const prior = inputs?.prior ?? null;
        return { status: prior ? "ready" : "none", prior, capturedAt: prior?.capturedAt ?? null, retry: noop };
    }
    const capturedAt = target?.captured_at ?? null;
    if (!enabled || !targetId) return { status: "none", prior: null, capturedAt, retry };
    // Before the effect has started this scan's request, it is loading, not "none".
    if (loaded.targetId !== targetId) return { status: "loading", prior: null, capturedAt, retry };
    return { status: loaded.status, prior: loaded.prior, capturedAt, retry };
}
